import { normalizeCountryCode, type CountryCode } from './country-code';

/** VAT classification of a country: EU member state or anything else. */
export const CountryVatRegion = {
  EU: 'EU',
  NON_EU: 'NON_EU'
} as const;

export type CountryVatRegion = (typeof CountryVatRegion)[keyof typeof CountryVatRegion];

/**
 * Per-country EU VAT ID regex patterns (full VAT ID including country prefix).
 * Sources: https://ec.europa.eu/taxation_customs/vies/ — key "EL" is used for Greece, not "GR".
 */
export const EU_VAT_PATTERNS = {
  AT: /^ATU[0-9]{8}$/,
  BE: /^BE[01]?[0-9]{9}$/,
  BG: /^BG[0-9]{9,10}$/,
  CY: /^CY[0-9]{8}[A-Z]$/,
  CZ: /^CZ[0-9]{8,10}$/,
  DE: /^DE[0-9]{9}$/,
  DK: /^DK[0-9]{8}$/,
  EE: /^EE[0-9]{9}$/,
  EL: /^EL[0-9]{9}$/,
  ES: /^ES[A-Z0-9][0-9]{7}[A-Z0-9]$/,
  FI: /^FI[0-9]{8}$/,
  FR: /^FR[A-HJ-NP-Z0-9]{2}[0-9]{9}$/,
  HR: /^HR[0-9]{11}$/,
  HU: /^HU[0-9]{8}$/,
  IE: /^IE[0-9][A-Z0-9+*][0-9]{5}[A-Z]{1,2}$/,
  IT: /^IT[0-9]{11}$/,
  LT: /^LT(?:[0-9]{9}|[0-9]{12})$/,
  LU: /^LU[0-9]{8}$/,
  LV: /^LV[0-9]{11}$/,
  MT: /^MT[0-9]{8}$/,
  NL: /^NL[0-9]{9}B[0-9]{2}$/,
  PL: /^PL[0-9]{10}$/,
  PT: /^PT[0-9]{9}$/,
  RO: /^RO[0-9]{2,10}$/,
  SE: /^SE[0-9]{12}$/,
  SI: /^SI[0-9]{8}$/,
  SK: /^SK[0-9]{10}$/
} as const;

/** EU VAT prefix — ISO alpha-2 code of a member state, except 'EL' for Greece. */
export type EuVatPrefix = keyof typeof EU_VAT_PATTERNS;

const EU_COUNTRY_CODES = new Set(
  Object.keys(EU_VAT_PATTERNS).map(code => (code === 'EL' ? 'GR' : code))
);

/**
 * Classifies a country for VAT logic: EU member state, NON_EU for any other
 * country, null for missing input. Expects a canonical CountryCode.
 */
export function getCountryVatRegion(countryCode?: CountryCode | null): CountryVatRegion | null {
  if (!countryCode) return null;
  return EU_COUNTRY_CODES.has(countryCode) ? CountryVatRegion.EU : CountryVatRegion.NON_EU;
}

/** True when the given country is an EU member state. */
export function isEuMember(value?: CountryCode | null): boolean {
  return getCountryVatRegion(value) === CountryVatRegion.EU;
}

/**
 * Returns the 2-letter prefix of a VAT ID (trim/uppercase), or undefined if too short.
 * Raw prefix — Greece stays 'EL' (its EU VAT prefix), not the ISO 'GR'.
 */
export function getVatIdPrefix(vatId: string | null | undefined): string | undefined {
  const normalized = vatId?.toUpperCase().trim();
  if (!normalized || normalized.length < 2) return undefined;
  return normalized.substring(0, 2);
}

/**
 * Derives the ISO country code from a full EU VAT ID (e.g. "CZ09834001" → "CZ").
 * Returns undefined unless the whole ID matches the country's VAT pattern.
 */
export function countryFromVatId(vatId: string | null | undefined): CountryCode | undefined {
  const normalized = vatId?.toUpperCase().trim();
  const prefix = getVatIdPrefix(normalized);
  if (!normalized || !prefix) return undefined;

  const pattern = EU_VAT_PATTERNS[prefix as EuVatPrefix];
  if (!pattern?.test(normalized)) return undefined;

  return normalizeCountryCode(prefix === 'EL' ? 'GR' : prefix);
}
